import React, { useState, useEffect } from 'react';
import { 
    Dialog, 
    DialogTitle, 
    DialogContent, 
    DialogActions, 
    Button, 
    Table, 
    TableBody, 
    TableCell, 
    TableContainer, 
    TableHead, 
    TableRow, 
    Paper, 
    TextField, 
    Box,
    Typography,
    Chip
} from '@mui/material';
import { readDICOMFile } from '../utils/cornerstoneInit';

/**
 * Dialog component to display DICOM tags for a selected SOP
 */
const DicomTagsDialog = ({ open, onClose, dicomFile, selectedFile }) => {
    const [dicomTags, setDicomTags] = useState([]);
    const [filteredTags, setFilteredTags] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    // Load DICOM tags when dialog opens
    useEffect(() => {
        if (open && (dicomFile || selectedFile)) {
            loadDicomTags();
        }
    }, [open, dicomFile, selectedFile]);

    // Filter tags based on search term
    useEffect(() => {
        if (searchTerm.trim() === '') {
            setFilteredTags(dicomTags);
        } else {
            const filtered = dicomTags.filter(tag => 
                tag.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                tag.tag.toLowerCase().includes(searchTerm.toLowerCase()) ||
                tag.value.toLowerCase().includes(searchTerm.toLowerCase())
            );
            setFilteredTags(filtered);
        }
    }, [searchTerm, dicomTags]);

    const loadDicomTags = async () => {
        setIsLoading(true);
        setError(null);
        setDicomTags([]);

        try {
            const fileToProcess = dicomFile || selectedFile;
            console.log('=== DICOM Tags Dialog Debug ===');
            console.log('Full fileToProcess object:', fileToProcess);
            console.log('Object keys:', Object.keys(fileToProcess || {}));
            console.log('dicomFile:', dicomFile);
            console.log('selectedFile:', selectedFile);
            
            // Check if this is truly an AHI file (from AWS HealthImaging) or a local DICOM file
            // AHI files should have imageId starting with 'ahi:' or specific AHI properties
            const isTrueAHI = fileToProcess.isAHI && (
                (fileToProcess.imageId && fileToProcess.imageId.startsWith('ahi:')) ||
                (fileToProcess.ahiMetadata) ||
                (fileToProcess.dicomMetadata) ||
                (!fileToProcess.file) // No local file means it's from AHI
            );
            
            console.log('File analysis:', {
                isAHI: fileToProcess.isAHI,
                isTrueAHI: isTrueAHI,
                hasFile: !!fileToProcess.file,
                fileType: typeof fileToProcess.file,
                imageId: fileToProcess.imageId,
                hasAhiMetadata: !!fileToProcess.ahiMetadata,
                hasDicomMetadata: !!fileToProcess.dicomMetadata,
                transferSyntax: fileToProcess.transferSyntax,
                transferSyntaxDesc: fileToProcess.transferSyntaxDesc,
                sopInstanceUID: fileToProcess.sopInstanceUID,
                filename: fileToProcess.filename
            });
            
            if (isTrueAHI) {
                // Handle AHI files - try multiple sources for metadata
                let metadata = null;
                
                // First, try direct ahiMetadata property
                if (fileToProcess.ahiMetadata) {
                    console.log('Found ahiMetadata in fileToProcess:', fileToProcess.ahiMetadata);
                    metadata = fileToProcess.ahiMetadata;
                } 
                // Second, try dicomMetadata property (for AHI videos)
                else if (fileToProcess.dicomMetadata) {
                    console.log('Found dicomMetadata in fileToProcess:', fileToProcess.dicomMetadata);
                    metadata = fileToProcess.dicomMetadata;
                }
                // Third, try to get from AHI cache using imageId
                else if (fileToProcess.imageId && window.ahiImageCache && window.ahiImageCache[fileToProcess.imageId]) {
                    console.log('Found metadata in AHI cache for imageId:', fileToProcess.imageId);
                    const cachedData = window.ahiImageCache[fileToProcess.imageId];
                    metadata = cachedData.metadata;
                }
                // Fourth, try to find in cache using filename for videos
                else if (fileToProcess.filename && window.ahiImageCache) {
                    console.log('Searching AHI cache by filename:', fileToProcess.filename);
                    // Look for cached entries that might match this file
                    for (const [cacheKey, cacheData] of Object.entries(window.ahiImageCache)) {
                        if (cacheKey.includes(fileToProcess.filename) || 
                            (cacheData.metadata && cacheData.metadata.filename === fileToProcess.filename)) {
                            console.log('Found matching cache entry:', cacheKey, cacheData);
                            metadata = cacheData.metadata;
                            break;
                        }
                    }
                }
                
                // If no metadata found yet, try transfer syntax matching
                if (!metadata && window.ahiImageCache && fileToProcess.transferSyntaxUID) {
                    console.log('Searching AHI cache by transfer syntax and properties:', fileToProcess.transferSyntaxUID);
                    // Look for cached entries with matching transfer syntax
                    for (const [cacheKey, cacheData] of Object.entries(window.ahiImageCache)) {
                        if (cacheData.metadata && 
                            cacheData.metadata.TransferSyntaxUID === fileToProcess.transferSyntaxUID &&
                            cacheData.metadata.NumberOfFrames === fileToProcess.numberOfFrames) {
                            console.log('Found matching cache entry by transfer syntax:', cacheKey, cacheData);
                            metadata = cacheData.metadata;
                            break;
                        }
                    }
                }
                
                // If still no metadata, try to find any single frame AHI entry
                if (!metadata && window.ahiImageCache && fileToProcess.numberOfFrames === 1) {
                    console.log('Searching AHI cache for single frame images');
                    // Look for any single frame AHI entries
                    for (const [cacheKey, cacheData] of Object.entries(window.ahiImageCache)) {
                        if (cacheKey.startsWith('ahi:') && 
                            !cacheKey.includes('video') &&
                            cacheData.metadata && 
                            (cacheData.metadata.NumberOfFrames === 1 || !cacheData.metadata.NumberOfFrames)) {
                            console.log('Found single frame AHI cache entry:', cacheKey, cacheData);
                            metadata = cacheData.metadata;
                            break;
                        }
                    }
                }
                
                // Last resort: create basic metadata from fileInfo properties
                if (!metadata && (fileToProcess.transferSyntaxUID || fileToProcess.sopInstanceUID || fileToProcess.numberOfFrames)) {
                    console.log('Creating metadata from fileInfo properties');
                    metadata = {
                        TransferSyntaxUID: fileToProcess.transferSyntaxUID,
                        SOPInstanceUID: fileToProcess.sopInstanceUID,
                        NumberOfFrames: fileToProcess.numberOfFrames,
                        SeriesDescription: fileToProcess.seriesDescription,
                        Filename: fileToProcess.filename,
                        Dimensions: fileToProcess.dimensions,
                        TransferSyntaxDescription: fileToProcess.transferSyntaxDesc
                    };
                }

                if (metadata) {
                    console.log('Converting metadata to tags:', metadata);
                    const tags = convertMetadataToTags(metadata);
                    setDicomTags(tags);
                } else {
                    console.error('No metadata found for AHI file. Available properties:', Object.keys(fileToProcess));
                    console.error('AHI cache contents:', window.ahiImageCache);
                    setError('No DICOM metadata found for this AHI file. The metadata may not have been loaded properly.');
                }
            } else if (fileToProcess.file) {
                // Handle local DICOM files
                const dicomData = await readDICOMFile(fileToProcess.file);
                const tags = extractDicomTags(dicomData);
                setDicomTags(tags);
            } else {
                setError('No file data available');
            }
        } catch (err) {
            console.error('Error loading DICOM tags:', err);
            setError(`Error loading DICOM tags: ${err.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    // Convert AHI metadata to tag format
    const convertMetadataToTags = (metadata) => {
        const tags = [];
        
        // Common DICOM tag mappings
        const tagMappings = {
            'SOPInstanceUID': { tag: '(0008,0018)', name: 'SOP Instance UID' },
            'SOPClassUID': { tag: '(0008,0016)', name: 'SOP Class UID' },
            'StudyInstanceUID': { tag: '(0020,000D)', name: 'Study Instance UID' },
            'SeriesInstanceUID': { tag: '(0020,000E)', name: 'Series Instance UID' },
            'PatientName': { tag: '(0010,0010)', name: 'Patient Name' },
            'PatientID': { tag: '(0010,0020)', name: 'Patient ID' },
            'StudyDate': { tag: '(0008,0020)', name: 'Study Date' },
            'StudyTime': { tag: '(0008,0030)', name: 'Study Time' },
            'Modality': { tag: '(0008,0060)', name: 'Modality' },
            'SeriesDescription': { tag: '(0008,103E)', name: 'Series Description' },
            'InstanceNumber': { tag: '(0020,0013)', name: 'Instance Number' },
            'SeriesNumber': { tag: '(0020,0011)', name: 'Series Number' },
            'Rows': { tag: '(0028,0010)', name: 'Rows' },
            'Columns': { tag: '(0028,0011)', name: 'Columns' },
            'NumberOfFrames': { tag: '(0028,0008)', name: 'Number of Frames' },
            'TransferSyntaxUID': { tag: '(0002,0010)', name: 'Transfer Syntax UID' },
            'PhotometricInterpretation': { tag: '(0028,0004)', name: 'Photometric Interpretation' },
            'SamplesPerPixel': { tag: '(0028,0002)', name: 'Samples per Pixel' },
            'BitsAllocated': { tag: '(0028,0100)', name: 'Bits Allocated' },
            'BitsStored': { tag: '(0028,0101)', name: 'Bits Stored' },
            'HighBit': { tag: '(0028,0102)', name: 'High Bit' },
            'PixelRepresentation': { tag: '(0028,0103)', name: 'Pixel Representation' }
        };

        // Process known tags
        Object.entries(metadata).forEach(([key, value]) => {
            if (tagMappings[key]) {
                tags.push({
                    tag: tagMappings[key].tag,
                    name: tagMappings[key].name,
                    value: String(value || ''),
                    vr: 'Unknown'
                });
            }
        });

        // Add any additional metadata that doesn't match known tags
        Object.entries(metadata).forEach(([key, value]) => {
            if (!tagMappings[key] && value !== null && value !== undefined) {
                tags.push({
                    tag: 'Unknown',
                    name: key,
                    value: String(value),
                    vr: 'Unknown'
                });
            }
        });

        // Sort tags by tag number
        tags.sort((a, b) => {
            if (a.tag === 'Unknown' && b.tag !== 'Unknown') return 1;
            if (a.tag !== 'Unknown' && b.tag === 'Unknown') return -1;
            if (a.tag === 'Unknown' && b.tag === 'Unknown') return a.name.localeCompare(b.name);
            return a.tag.localeCompare(b.tag);
        });

        return tags;
    };

    // Extract DICOM tags from parsed DICOM data
    const extractDicomTags = (dicomData) => {
        const tags = [];
        
        // Get all elements from the dataset
        Object.keys(dicomData.elements).forEach(tagKey => {
            const element = dicomData.elements[tagKey];
            
            // Format tag as (GGGG,EEEE)
            const group = tagKey.substring(1, 5).toUpperCase();
            const elem = tagKey.substring(5, 9).toUpperCase();
            const formattedTag = `(${group},${elem})`;
            
            // Get tag name from dictionary or use tag key
            const tagName = getTagName(group, elem) || tagKey;
            
            // Get value
            let value = '';
            try {
                if (element.vr === 'SQ') {
                    value = '[Sequence]';
                } else if (element.length === 0) {
                    value = '';
                } else {
                    // Try different value extraction methods
                    value = dicomData.string(tagKey) || 
                           dicomData.text(tagKey) || 
                           dicomData.floatString(tagKey) || 
                           dicomData.intString(tagKey) || 
                           '[Binary Data]';
                }
            } catch (e) {
                value = '[Unable to parse]';
            }

            tags.push({
                tag: formattedTag,
                name: tagName,
                value: String(value),
                vr: element.vr || 'UN'
            });
        });

        // Sort by tag
        tags.sort((a, b) => a.tag.localeCompare(b.tag));
        
        return tags;
    };

    // Basic tag name dictionary (subset of common tags)
    const getTagName = (group, element) => {
        const tagDict = {
            '0002,0000': 'File Meta Information Group Length',
            '0002,0001': 'File Meta Information Version',
            '0002,0002': 'Media Storage SOP Class UID',
            '0002,0003': 'Media Storage SOP Instance UID',
            '0002,0010': 'Transfer Syntax UID',
            '0002,0012': 'Implementation Class UID',
            '0002,0013': 'Implementation Version Name',
            '0008,0005': 'Specific Character Set',
            '0008,0008': 'Image Type',
            '0008,0016': 'SOP Class UID',
            '0008,0018': 'SOP Instance UID',
            '0008,0020': 'Study Date',
            '0008,0030': 'Study Time',
            '0008,0050': 'Accession Number',
            '0008,0060': 'Modality',
            '0008,0070': 'Manufacturer',
            '0008,0080': 'Institution Name',
            '0008,0090': 'Referring Physician Name',
            '0008,103E': 'Series Description',
            '0008,1030': 'Study Description',
            '0010,0010': 'Patient Name',
            '0010,0020': 'Patient ID',
            '0010,0030': 'Patient Birth Date',
            '0010,0040': 'Patient Sex',
            '0018,0050': 'Slice Thickness',
            '0018,0060': 'KVP',
            '0018,1030': 'Protocol Name',
            '0020,000D': 'Study Instance UID',
            '0020,000E': 'Series Instance UID',
            '0020,0010': 'Study ID',
            '0020,0011': 'Series Number',
            '0020,0013': 'Instance Number',
            '0020,0032': 'Image Position Patient',
            '0020,0037': 'Image Orientation Patient',
            '0028,0002': 'Samples per Pixel',
            '0028,0004': 'Photometric Interpretation',
            '0028,0008': 'Number of Frames',
            '0028,0010': 'Rows',
            '0028,0011': 'Columns',
            '0028,0030': 'Pixel Spacing',
            '0028,0100': 'Bits Allocated',
            '0028,0101': 'Bits Stored',
            '0028,0102': 'High Bit',
            '0028,0103': 'Pixel Representation',
            '0028,1050': 'Window Center',
            '0028,1051': 'Window Width',
            '7FE0,0010': 'Pixel Data'
        };

        return tagDict[`${group},${element}`];
    };

    const handleClose = () => {
        setSearchTerm('');
        setDicomTags([]);
        setFilteredTags([]);
        setError(null);
        onClose();
    };

    const getFileName = () => {
        const fileToProcess = dicomFile || selectedFile;
        return fileToProcess?.filename || 'Unknown File';
    };

    return (
        <Dialog 
            open={open} 
            onClose={handleClose} 
            maxWidth="lg" 
            fullWidth
            PaperProps={{
                sx: {
                    backgroundColor: '#1a1a1a',
                    color: '#e0e0e0'
                }
            }}
        >
            <DialogTitle sx={{ color: '#e0e0e0', borderBottom: '1px solid #333' }}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Typography variant="h6">
                        DICOM Tags - {getFileName()}
                    </Typography>
                    <Chip 
                        label={`${filteredTags.length} tags`} 
                        size="small" 
                        sx={{ 
                            backgroundColor: '#4285f4', 
                            color: 'white' 
                        }} 
                    />
                </Box>
            </DialogTitle>
            
            <DialogContent sx={{ backgroundColor: '#1a1a1a', padding: 2 }}>
                {/* Search field */}
                <Box mb={2}>
                    <TextField
                        fullWidth
                        variant="outlined"
                        placeholder="Search tags by name, tag, or value..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        size="small"
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                backgroundColor: '#2a2a2a',
                                color: '#e0e0e0',
                                '& fieldset': {
                                    borderColor: '#444',
                                },
                                '&:hover fieldset': {
                                    borderColor: '#555',
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: '#4285f4',
                                },
                            },
                            '& .MuiInputBase-input::placeholder': {
                                color: '#888',
                                opacity: 1,
                            },
                        }}
                    />
                </Box>

                {isLoading && (
                    <Box display="flex" justifyContent="center" p={3}>
                        <Typography color="#888">Loading DICOM tags...</Typography>
                    </Box>
                )}

                {error && (
                    <Box p={2} bgcolor="#2a1a1a" border="1px solid #d32f2f" borderRadius={1}>
                        <Typography color="#f44336">{error}</Typography>
                    </Box>
                )}

                {!isLoading && !error && filteredTags.length > 0 && (
                    <TableContainer 
                        component={Paper} 
                        sx={{ 
                            backgroundColor: '#2a2a2a',
                            maxHeight: 500,
                            '& .MuiTableCell-root': {
                                borderColor: '#444'
                            }
                        }}
                    >
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ backgroundColor: '#333', color: '#e0e0e0', fontWeight: 'bold', width: '40%' }}>
                                        Name
                                    </TableCell>
                                    <TableCell sx={{ backgroundColor: '#333', color: '#e0e0e0', fontWeight: 'bold', width: '60%' }}>
                                        Value
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredTags.map((tag, index) => (
                                    <TableRow 
                                        key={index}
                                        sx={{
                                            '&:nth-of-type(odd)': {
                                                backgroundColor: '#242424',
                                            },
                                            '&:nth-of-type(even)': {
                                                backgroundColor: '#2a2a2a',
                                            },
                                            '&:hover': {
                                                backgroundColor: '#333',
                                            }
                                        }}
                                    >
                                        <TableCell sx={{ color: '#e0e0e0', fontWeight: '500' }}>
                                            {tag.name}
                                        </TableCell>
                                        <TableCell sx={{ 
                                            color: '#e0e0e0', 
                                            wordBreak: 'break-all',
                                            fontFamily: 'monospace',
                                            fontSize: '0.9em'
                                        }}>
                                            {tag.value}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                {!isLoading && !error && filteredTags.length === 0 && dicomTags.length > 0 && (
                    <Box display="flex" justifyContent="center" p={3}>
                        <Typography color="#888">No tags match your search criteria</Typography>
                    </Box>
                )}
            </DialogContent>
            
            <DialogActions sx={{ backgroundColor: '#242424', borderTop: '1px solid #333' }}>
                <Button 
                    onClick={handleClose} 
                    sx={{ 
                        color: '#e0e0e0',
                        '&:hover': {
                            backgroundColor: '#333'
                        }
                    }}
                >
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DicomTagsDialog;
